'use client';

import { useState } from 'react';
import { AppIcon } from '@impacta/shared';
import { useCart } from '@/contexts/CartContext';
import { mapVariantsToSessions } from '@/lib/shopify/mappers';
import { formatCLP } from '@/lib/format';
import type { ShopifyVariant } from '@/lib/shopify/types/storefront';
import { SessionSelector } from '@impacta/shared';
import GiftWorkshopModal from './GiftWorkshopModal';

interface Props {
  variants: ShopifyVariant[];
  price: string;
  workshopTitle: string;
  workshopLocation: string;
}

export default function CartCTAPanel({ variants, price, workshopTitle, workshopLocation }: Props) {
  const { addItem, openDrawer, loading: cartLoading } = useCart();
  const sessions = mapVariantsToSessions(variants);

  // Default to first available session, or first if none available
  const firstAvailable = sessions.find((s) => s.available) ?? sessions[0];
  const [selectedId, setSelectedId] = useState(firstAvailable?.id ?? '');
  const [ctaHover, setCtaHover] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);

  const selected = sessions.find((s) => s.id === selectedId);
  const allSoldOut = sessions.every((s) => !s.available);
  const ctaEnabled = !allSoldOut && selected?.available && !cartLoading;
  const giftEnabled = !allSoldOut && selected?.available && !cartLoading;

  const handleReservar = async () => {
    if (!selected || !selected.available) return;
    await addItem(selected.id, 1, [{ key: '_workshop_location', value: workshopLocation }]);
    openDrawer();
  };

  async function handleGiftSubmit(attributes: Array<{ key: string; value: string }>) {
    if (!selected || !selected.available) return;
    await addItem(selected.id, 1, [
      ...attributes,
      { key: '_workshop_location', value: workshopLocation },
    ]);
    openDrawer();
    setShowGiftModal(false);
  }

  return (
    <div className="sticky-cta-panel">
      {/* Price */}
      <div className="flex items-baseline gap-2 mb-5">
        <span className="text-3xl font-extrabold text-[#373A36]">{selected?.price ?? formatCLP(price)}</span>
        <span className="text-sm text-[#6b6e68]">CLP por persona</span>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-2 mb-5 pb-5 border-b border-[#E8E8E4]">
        <div className="flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <AppIcon key={i} name="StarIcon" size={14} className="text-[#373A36]" variant="solid" />
          ))}
        </div>
        <span className="text-sm font-bold text-[#373A36]">5.0</span>
        <span className="text-sm text-[#6b6e68]">(Nuevo)</span>
      </div>

      {/* Session selection */}
      <SessionSelector sessions={sessions} selectedId={selectedId} onSelect={setSelectedId} />

      {/* Price for selected session */}
      {selected && (
        <div
          className="flex items-center justify-between mb-5 px-4 py-3 rounded-xl"
          style={{ backgroundColor: '#FAFAF8' }}
        >
          <span className="text-sm text-[#6b6e68]">Precio por persona</span>
          <span className="text-lg font-extrabold text-[#373A36]">{selected.price}</span>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleReservar}
        disabled={!ctaEnabled}
        onMouseEnter={() => ctaEnabled && setCtaHover(true)}
        onMouseLeave={() => setCtaHover(false)}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-base transition-all duration-200 mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: allSoldOut ? '#6b6e68' : ctaHover ? '#00A19B' : '#E0DF00',
          color: ctaHover && !allSoldOut ? 'white' : '#373A36',
        }}
        aria-label={
          allSoldOut ? 'Todas las sesiones estan agotadas' : 'Reservar mi cupo en esta actividad'
        }
      >
        <AppIcon name="TicketIcon" size={18} />
        {allSoldOut ? 'Agotado' : cartLoading ? 'Agregando...' : 'Reservar mi cupo'}
      </button>

      {/* Gift button — opens modal */}
      <button
        onClick={() => setShowGiftModal(true)}
        disabled={!giftEnabled}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-sm border-2 border-[#E8E8E4] text-[#6b6e68] hover:border-[#00A19B] hover:text-[#00A19B] transition-all duration-200 mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <AppIcon name="GiftIcon" size={16} />
        Regalar esta actividad
      </button>

      {/* Gift modal */}
      <GiftWorkshopModal
        open={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        onSubmit={handleGiftSubmit}
        workshopTitle={workshopTitle}
        sessionLabel={selected?.label ?? ''}
        loading={cartLoading}
      />

      {/* Trust signals */}
      <div className="mt-5 pt-5 border-t border-[#E8E8E4] space-y-2">
        {[
          { icon: 'ShieldCheckIcon', text: 'Pago 100% seguro' },
          { icon: 'ArrowUturnLeftIcon', text: 'Reembolso hasta 48 hrs antes' },
          { icon: 'EnvelopeIcon', text: 'Confirmacion inmediata por email' },
        ].map((item) => (
          <div key={item.text} className="flex items-center gap-2.5">
            <AppIcon name={item.icon as any} size={15} className="text-[#00A19B] flex-shrink-0" />
            <span className="text-xs text-[#6b6e68]">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
